<script setup lang="ts">
import { Points, ShaderMaterial, BufferGeometry, Vector3, Vector2 } from 'three'
import { useGLTF } from '@tresjs/cientos'
import { onMounted, ref, shallowRef } from 'vue'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js'
import { useTresContext, useLoop } from '@tresjs/core'

gsap.registerPlugin(ScrollTrigger)
const { onBeforeRender } = useLoop()

const path = '/models/logo.glb'
const { scene } = await useGLTF(path, { draco: false })

const logoDotsBackRef = shallowRef<Points>()
// const logoImagesRef = shallowRef<Points>()
const logoDotsFrontRef = shallowRef<Points>()
const logoDotsVeryFrontRef = shallowRef<Points>()

const scale = ref(3000)
const scaleStart = 300
const pointer = new Vector2(0, 0)

const sphereCenterBack = new Vector3(0, 0, 0)
const sphereCenterFront = new Vector3(0, 0, 0)
// const sphereCenterImages = new Vector3(0, 0, 0.2)
const sphereCenterVeryFront = new Vector3(0, 0, 0.2)

const sphereCenterBackTarget = new Vector3(0, 0, 0)
const sphereCenterFrontTarget = new Vector3(0, 0, 0)
// const sphereCenterImagesTarget = new Vector3(0, 0, -10)
const sphereCenterVeryFrontTarget = new Vector3(0, 0, -10)

const rotateX = shallowRef(0)
const rotateY = shallowRef(0)
const cameraRotateZ = shallowRef(0)

const { camera: cameraRef } = useTresContext()
const { isMobileOrTablet } = useDevice()

const dotsVertexShader = `
uniform float time;
uniform float size;
uniform float uSphereRadius;
uniform vec3 uSphereCenter;

uniform float uLoadScale;

varying float vSinVal;
varying float vStrength;

void main() {
  vec3 wPos = vec3(modelMatrix * vec4(position, 1.0));
  float dist = distance(wPos, uSphereCenter);
  float strength = 0.0;

  if (dist < uSphereRadius) {
    // 1) Normal sphere logic (strength in [0..1])
    strength = 1.0 - (dist / uSphereRadius);

    // 2) Scale the push by a constant factor (0.15) AND by uLoadScale
    //    e.g., if uLoadScale = 1, it’s your normal push
    //          if uLoadScale = 2, everything’s pushed twice as far
    float pushAmount = 0.15 * strength * uLoadScale;

    // 3) Apply push
    wPos += normalize(wPos - uSphereCenter) * pushAmount;
  }
  vStrength = strength;

  // wPos.y -= time * 0.5;

  // Sine-based color factor
  float sinVal = sin(mod(wPos.y, 6.28318530718)) * 0.4 + 0.9;
  vSinVal = sinVal;

  float sizeChange = 0.8 + sinVal * 2.0;
  gl_PointSize = size * sizeChange;

  gl_Position = projectionMatrix * viewMatrix * vec4(wPos, 1.0);
}
`

const dotsFragmentShader = `
uniform float alpha;
varying float vSinVal;
varying float vStrength;

// Function to create a custom gradient based on vSinVal
vec3 customRainbow(float value) {
    if (value < 0.5) {
        // Transition from pink (1.0, 0.4, 0.9) to baby blue (0.8, 0.8, 1.0)
        return mix(vec3(1.0, 0.4, 0.9), vec3(0.5, 0.8, 1.0), value * 2.0);
    } else {
        // Transition from baby blue (0.8, 0.8, 1.0) to pink (1.0, 0.5, 0.7)
        return mix(vec3(0.5, 0.8, 1.0), vec3(1.0, 0.5, 0.7), (value - 0.5) * 2.0);
    }
}

void main() {
    // Round dot
    float dist = length(gl_PointCoord.xy - 0.5);
    if (dist > 0.5) discard;

    // Map vSinVal to custom rainbow colors
    vec3 color = customRainbow(vSinVal);

    gl_FragColor = vec4(color, alpha);
}


`

function createDotsMaterial() {
  return new ShaderMaterial({
    uniforms: {
      time: { value: 1 },
      alpha: { value: 1 },
      size: { value: 2.0 },
      uSphereCenter: { value: new Vector3(0, 0, 0) },
      uSphereRadius: { value: 500.0 },
      uLoadScale: { value: 100.0 } // can animate this from 0..N
    },
    vertexShader: dotsVertexShader,
    fragmentShader: dotsFragmentShader,
    transparent: true,
    depthWrite: false
  })
}

const dotsMaterialBack = createDotsMaterial()
const dotsMaterialFront = createDotsMaterial()

/* const textureLoader = new TextureLoader()
const texturePaths = [
  '/images/cropped-alpha1.png',
  '/images/cropped-alpha2.png',
  '/images/cropped-alpha3.png',
  '/images/cropped-alpha4.png',
  '/images/cropped-alpha5.png',
  '/images/cropped-alpha6.png',
  '/images/cropped-alpha1.png',
  '/images/cropped-alpha2.png',
  '/images/cropped-alpha3.png',
  '/images/cropped-alpha4.png'
]
const loadedTextures = texturePaths.map((path) => {
  const tex = textureLoader.load(path)
  tex.flipY = false
  return tex
})

function createTextureUniforms() {
  return {
    time: { value: 1 },
    alpha: { value: 1 },
    size: { value: 0.8 },
    uSphereCenter: { value: new Vector3(-10, 0, -5) },
    uSphereRadius: { value: 1.5 },
    uLoadScale: { value: 1 },

    uTexture0: { value: loadedTextures[0] },
    uTexture1: { value: loadedTextures[1] },
    uTexture2: { value: loadedTextures[2] },
    uTexture3: { value: loadedTextures[3] },
    uTexture4: { value: loadedTextures[4] },
    uTexture5: { value: loadedTextures[5] },
    uTexture6: { value: loadedTextures[6] },
    uTexture7: { value: loadedTextures[7] },
    uTexture8: { value: loadedTextures[8] },
    uTexture9: { value: loadedTextures[9] }
  }
}

const imagesVertexShader = `
  attribute float imageIndex;
  varying float vImageIndex;

  uniform float time;
  uniform float size;
  uniform vec3 uSphereCenter;
  uniform float uSphereRadius;

  void main() {
    vImageIndex = imageIndex;

    vec3 wPos = vec3(modelMatrix * vec4(position, 1.0));
    float dist = distance(wPos, uSphereCenter);
    if (dist < uSphereRadius) {
      float strength = 1.0 - (dist / uSphereRadius);
      // Lighter push factor
      wPos += normalize(wPos - uSphereCenter) * strength * 0.15;
      gl_PointSize = size * (3.0 + strength * 70.0);
    } else {
      gl_PointSize = size * 3.0;
    }

    // wPos.y -= time * 0.5;

    gl_Position = projectionMatrix * viewMatrix * vec4(wPos, 1.0);
  }
`

const imagesFragmentShader = `
  precision highp float;
  varying float vImageIndex;
  uniform float alpha;

  uniform sampler2D uTexture0;
  uniform sampler2D uTexture1;
  uniform sampler2D uTexture2;
  uniform sampler2D uTexture3;
  uniform sampler2D uTexture4;
  uniform sampler2D uTexture5;
  uniform sampler2D uTexture6;
  uniform sampler2D uTexture7;
  uniform sampler2D uTexture8;
  uniform sampler2D uTexture9;

  void main() {
    float distCoord = length(gl_PointCoord - 0.5);
    if (distCoord > 0.5) discard;

    vec2 uv = gl_PointCoord;
    int idx = int(floor(vImageIndex + 0.5));
    vec4 texColor;
    if      (idx == 0) texColor = texture2D(uTexture0, uv);
    else if (idx == 1) texColor = texture2D(uTexture1, uv);
    else if (idx == 2) texColor = texture2D(uTexture2, uv);
    else if (idx == 3) texColor = texture2D(uTexture3, uv);
    else if (idx == 4) texColor = texture2D(uTexture4, uv);
    else if (idx == 5) texColor = texture2D(uTexture5, uv);
    else if (idx == 6) texColor = texture2D(uTexture6, uv);
    else if (idx == 7) texColor = texture2D(uTexture7, uv);
    else if (idx == 8) texColor = texture2D(uTexture8, uv);
    else               texColor = texture2D(uTexture9, uv);

    if (texColor.a < 0.1) discard;
    gl_FragColor = vec4(texColor.rgb, texColor.a * alpha);
  }
`

const imagesMaterial = new ShaderMaterial({
  uniforms: createTextureUniforms(),
  vertexShader: imagesVertexShader,
  fragmentShader: imagesFragmentShader,
  transparent: true,
  depthWrite: false
}) */

const frontMostVertexShader = `
  uniform float time;
  uniform float size;
  uniform vec3 uSphereCenter;
  uniform float uSphereRadius;
  uniform float uLoadScale;

  varying float vStrength;

  void main() {
    vec3 wPos = vec3(modelMatrix * vec4(position, 1.0));
    float dist = distance(wPos, uSphereCenter);

    float strength = 0.0;

  if (dist < uSphereRadius) {
    // 1) Normal sphere logic (strength in [0..1])
    strength = 1.0 - (dist / uSphereRadius);

    // 2) Scale the push by a constant factor (0.15) AND by uLoadScale
    //    e.g., if uLoadScale = 1, it’s your normal push
    //          if uLoadScale = 2, everything’s pushed twice as far
    float pushAmount = 0.15 * strength * uLoadScale;

    // 3) Apply push
    wPos += normalize(wPos - uSphereCenter) * pushAmount;
  }
  vStrength = strength;

    // wave?
    // wPos.y -= time * 0.5;

    gl_PointSize = size * 5.0;

    gl_Position = projectionMatrix * viewMatrix * vec4(wPos, 1.0);
  }
`
const frontMostFragmentShader = `
  uniform float alpha;
  varying float vStrength;

  void main() {
    float dist = length(gl_PointCoord.xy - 0.5);
    if (dist > 0.5) discard;

    // color = white -> #A168FF
    vec3 color = mix(vec3(0.0), vec3(0.63, 0.41, 1.0), 1.0);

    float finalAlpha = alpha;
    // if strength=1 => alpha=0.2, if strength=0 => alpha=1
    finalAlpha = mix(finalAlpha, 0.5, vStrength);

    gl_FragColor = vec4(color, finalAlpha);
  }
`
function createFrontMostMaterial() {
  return new ShaderMaterial({
    uniforms: {
      time: { value: 1 },
      alpha: { value: 0.8 },
      size: { value: 1.0 },
      uSphereCenter: { value: new Vector3(-100, 0, 0) },
      uSphereRadius: { value: 1.0 }
    },
    vertexShader: frontMostVertexShader,
    fragmentShader: frontMostFragmentShader,
    transparent: true,
    depthWrite: false
  })
}
const dotsMaterialVeryFront = createFrontMostMaterial()

const o = scene.children[0]
if (!o || !o.isMesh || !o.geometry) {
  console.error('Invalid mesh or geometry in the loaded GLTF scene.')
} else {
  const sampler = new MeshSurfaceSampler(o).build()

  // LAYER 1 => DOTS (BACK)
  {
    const positionsDotsBack = new Array(isMobileOrTablet ? 1000 : 5000).fill(null).map(() => {
      const p = new Vector3()
      sampler.sample(p)
      return p
    })
    const dotsGeometryBack = new BufferGeometry().setFromPoints(positionsDotsBack)
    const dotsPointsBack = new Points(dotsGeometryBack, dotsMaterialBack)
    dotsPointsBack.position.set(0, 0, -1)
    logoDotsBackRef.value = dotsPointsBack
  }

  // LAYER 2 => IMAGES (MIDDLE)
  /* {
    const numPointsImages = 30
    const positionsImages = new Array(numPointsImages).fill(null).map(() => {
      const p = new Vector3()
      sampler.sample(p)
      return p
    })
    const imageIndices = new Float32Array(numPointsImages)
    for (let i = 0; i < numPointsImages; i++) {
      imageIndices[i] = Math.floor(Math.random() * 10)
    }
    const imagesGeometry = new BufferGeometry().setFromPoints(positionsImages)
    imagesGeometry.setAttribute('imageIndex', new BufferAttribute(imageIndices, 1))
    const imagesPoints = new Points(imagesGeometry, imagesMaterial)
    imagesPoints.position.set(0, 0, 0)
    logoImagesRef.value = imagesPoints
  } */

  // LAYER 3 => DOTS (FRONT)
  {
    const positionsDotsFront = new Array(isMobileOrTablet ? 400 : 800).fill(null).map(() => {
      const p = new Vector3()
      sampler.sample(p)
      return p
    })
    const dotsGeometryFront = new BufferGeometry().setFromPoints(positionsDotsFront)
    const dotsPointsFront = new Points(dotsGeometryFront, dotsMaterialFront)
    dotsPointsFront.position.set(0, 0, 2)
    logoDotsFrontRef.value = dotsPointsFront
  }

  // LAYER 4 => VERY FRONT
  {
    const positionsDotsVeryFront = new Array(isMobileOrTablet ? 200 : 500).fill(null).map(() => {
      const p = new Vector3()
      sampler.sample(p)
      return p
    })
    const dotsGeometryVeryFront = new BufferGeometry().setFromPoints(positionsDotsVeryFront)
    const dotsPointsVeryFront = new Points(dotsGeometryVeryFront, dotsMaterialVeryFront)
    dotsPointsVeryFront.position.set(0, 0, 1.2)
    logoDotsVeryFrontRef.value = dotsPointsVeryFront
  }
}

onMounted(() => {
  gsap.to(dotsMaterialBack.uniforms.uLoadScale, {
    value: 1,
    duration: 3.5,
    ease: 'power2.out'
  })

  gsap.to(dotsMaterialFront.uniforms.uLoadScale, {
    value: 1,
    duration: 3.5,
    ease: 'power2.out'
  })

  /* if (logoImagesRef.value) {
    logoImagesRef.value.visible = true
    imagesMaterial.uniforms.alpha.value = 0
  } */

  // Standard lerp
  function lerp(start: number, end: number, t: number) {
    return start * (1 - t) + end * t
  }

  function animateIn() {
    const startTime = performance.now()
    const duration = 2.5

    function ease(t: number) {
      // Strong ease-out curve
      return 1 - Math.pow(1 - t, 3) // Higher exponent makes it stronger
    }

    function frame() {
      const elapsed = (performance.now() - startTime) / 1000
      const progress = Math.min(elapsed / duration, 1)

      const eased = ease(progress)

      scale.value = lerp(5000, scaleStart, eased)

      if (progress < 1) {
        requestAnimationFrame(frame)
      }
      /* else {
        gsap.to(imagesMaterial.uniforms.alpha, {
          value: 1,
          duration: 1,
          ease: 'power2.inOut'
        })
      } */
    }

    frame()
  }
  animateIn()

  ScrollTrigger.create({
    trigger: document.body,
    start: 'top top',
    end: 'bottom bottom',
    scrub: isMobileOrTablet ? 0.5 : true,
    onUpdate: (self) => {
      scale.value = scaleStart + self.progress * 5000
      dotsMaterialBack.uniforms.uLoadScale.value = 1 + self.progress * 300
      // imagesMaterial.uniforms.uLoadScale.value = 1 + self.progress * 1000
      // imagesMaterial.uniforms.alpha.value = 1 - self.progress * 10
      if (!isMobileOrTablet) {
        cameraRotateZ.value = self.progress * 10
      }
    }
  })

  function handlePointerMove(event: MouseEvent) {
    pointer.x = (event.clientX / window.innerWidth) * 2 - 1
    pointer.y = -(event.clientY / window.innerHeight) * 2 + 1

    sphereCenterBackTarget.set(pointer.x * 1.2, pointer.y * 1.2, 0)
    sphereCenterFrontTarget.set(pointer.x * 1.5, pointer.y * 1.5, 0)
    // sphereCenterImagesTarget.set(pointer.x * 1.5, pointer.y * 1.5, 0.2)
    sphereCenterVeryFrontTarget.set(pointer.x * 1.0, pointer.y * 1.0, 0.2)

    rotateX.value = lerp(rotateX.value, pointer.y * 0.05, 0.1)
    rotateY.value = lerp(rotateY.value, pointer.x * -0.05, 0.1)
  }

  setTimeout(() => {
    window.addEventListener('pointermove', handlePointerMove)
  }, 1500)

  if (isMobileOrTablet) {
    sphereCenterBackTarget.set(pointer.x * 0.8, pointer.y * 0.8, -4)
    sphereCenterFrontTarget.set(pointer.x * 0.8, pointer.y * 0.8, -4)
    // sphereCenterImagesTarget.set(pointer.x * 1.5, pointer.y * 1.5, -4)
    sphereCenterVeryFrontTarget.set(pointer.x * 1.0, pointer.y * 1.0, -0.2)

    setInterval(() => {
      gsap.to(sphereCenterBackTarget, {
        x: Math.random() * 1.6 - 0.8,
        y: Math.random() * 1.6 - 0.8,
        duration: 2,
        ease: 'power2.inOut',
        repeat: -1,
        yoyo: true
      })
      gsap.to(sphereCenterFrontTarget, {
        x: Math.random() * 1.6 - 0.8,
        y: Math.random() * 1.6 - 0.8,
        duration: 2,
        ease: 'power2.inOut',
        repeat: -1,
        yoyo: true
      })
      /* gsap.to(sphereCenterImagesTarget, {
        x: Math.random() * 3.0 - 1.5,
        y: Math.random() * 3.0 - 1.5,
        duration: 2,
        ease: 'power2.inOut',
        repeat: -1,
        yoyo: true
      }) */
      gsap.to(sphereCenterVeryFrontTarget, {
        x: Math.random() * 2.0 - 1.0,
        y: Math.random() * 2.0 - 1.0,
        duration: 2,
        ease: 'power2.inOut',
        repeat: -1,
        yoyo: true
      })
    }, 5000)
  }
})

onBeforeRender(() => {
  if (!cameraRef.value) return
  const cam = cameraRef.value

  if (!isMobileOrTablet) {
    const baseX = 0
    const baseY = -0.5
    const targetX = baseX + pointer.x * 0.5
    const targetY = baseY + pointer.y * 0.5
    cam.position.x += 0.05 * (targetX - cam.position.x)
    cam.position.y += 0.05 * (targetY - cam.position.y)
  }

  sphereCenterBack.lerp(sphereCenterBackTarget, 0.4)
  sphereCenterFront.lerp(sphereCenterFrontTarget, 0.9)
  // sphereCenterImages.lerp(sphereCenterImagesTarget, 0.1)
  sphereCenterVeryFront.lerp(sphereCenterVeryFrontTarget, 0.5)
  // Update uniforms
  dotsMaterialBack.uniforms.uSphereCenter.value.copy(sphereCenterBack)
  dotsMaterialFront.uniforms.uSphereCenter.value.copy(sphereCenterFront)
  // imagesMaterial.uniforms.uSphereCenter.value.copy(sphereCenterImages)
  dotsMaterialVeryFront.uniforms.uSphereCenter.value.copy(sphereCenterVeryFront)

  // Animate the time uniform
  dotsMaterialBack.uniforms.time.value += 0.01
  // imagesMaterial.uniforms.time.value += 0.01
  dotsMaterialFront.uniforms.time.value += 0.01
  dotsMaterialVeryFront.uniforms.time.value += 0.01
})
</script>

<template>
  <TresGroup>
    <TresPerspectiveCamera
      ref="cameraRef"
      :position="[0, -0.5, 5]"
      :rotation="[0, 0, cameraRotateZ]"
    />

    <!-- 
    <primitive :object="mouseTrailLine" />
    -->

    <Levioso :speed="2" :position="[0, 0, isMobileOrTablet ? -4 : 0]">
      <primitive
        v-if="logoDotsBackRef"
        :object="logoDotsBackRef"
        :scale="[scale, scale, scale]"
        :rotation="[Math.PI / 2 + rotateX, rotateY, 0]"
        :position="[-0.5, -1.0, 0]"
      />

      <!--
      <primitive
        v-if="logoImagesRef"
        :object="logoImagesRef"
        :scale="[scale, scale, scale]"
        :rotation="[Math.PI / 2 + rotateX, rotateY, 0]"
        :position="[-0.5, -1.0, 0.1]"
      /> -->

      <primitive
        v-if="logoDotsFrontRef"
        :object="logoDotsFrontRef"
        :scale="[scale * 1.03, scale * 1.03, scale * 1.03]"
        :rotation="[Math.PI / 2 + rotateX, rotateY, 0]"
        :position="[-0.5, -1.0, 0.45]"
      />
    </Levioso>

    <primitive
      v-if="logoDotsVeryFrontRef"
      :object="logoDotsVeryFrontRef"
      :scale="[scale, scale, scale]"
      :rotation="[Math.PI / 2, 0, 0]"
      :position="[-0.4, -0.8, isMobileOrTablet ? 3 : 4]"
    />
  </TresGroup>
</template>
